import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Grid,
  Paper,
  Typography,
  Card,
  CardContent,
  LinearProgress,
  Avatar,
  Chip,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  IconButton,
  Button,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Select,
  FormControl,
  InputLabel,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import {
  TrendingUp,
  People,
  Message,
  Euro,
  ArrowUpward,
  ArrowDownward,
  MoreVert,
  Phone,
  Email as EmailIcon,
  Schedule,
  PersonAdd,
  AssignmentInd,
  DirectionsCar,
  WhatsApp,
  Sms,
  CloudUpload,
  ContactPhone,
  Handshake,
  DoNotDisturb,
  CalendarToday,
  PersonOff,
  CheckCircle,
  Cancel,
} from '@mui/icons-material';
import { useLeadStore } from '../stores/leadStore';
import { useAuthStore } from '../stores/authStore';
import { useStaffStore } from '../stores/staffStore';
import CustomerUpload from '../components/CustomerUpload';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from 'recharts';

// Remove mock data - using real stores now

// Stats will be calculated from real data
const getStatsCards = (leadCount: number) => [
  {
    title: 'Available Leads',
    value: leadCount.toString(),
    change: '+0%',
    trend: 'up',
    icon: <People />,
    color: '#1a237e',
    description: 'Total number of not contacted leads in the platform',
  },
  {
    title: 'Contacted Leads',
    value: '0',
    change: '+0%',
    trend: 'up',
    icon: <ContactPhone />,
    color: '#2196F3',
    description: 'Number of total leads that have been contacted by the AI',
  },
  {
    title: 'Handed Over',
    value: '0',
    change: '+0%',
    trend: 'up',
    icon: <Handshake />,
    color: '#4CAF50',
    description: 'Number of leads that were qualified and got handed over to the human sales team',
  },
  {
    title: 'Dropped',
    value: '0',
    change: '+0%',
    trend: 'up',
    icon: <PersonOff />,
    color: '#FF5722',
    description: 'Number of leads that were not interested or unresponsive',
  },
];

// These will be populated from real data when available
const leadSourcesData: any[] = [];

// These will be populated from real leads
const resolveRecentLeadStatus = (lead: any): string => {
  const upstreamStatus = (lead.originalStatus || lead.stage || '').toLowerCase();
  switch (upstreamStatus) {
    case 'qualified':
      if (lead.interestLevel === 'high') return 'high_interest';
      if (lead.interestLevel === 'medium') return 'interested';
      return 'qualified';
    case 'engaging':
      return 'engaging';
    case 'assigned':
      return 'assigned';
    case 'in_contact':
      return 'in_contact';
    case 'not_interested':
    case 'lost':
      return 'not_interested';
    case 'unassigned':
    case 'queued':
      if (lead.interestLevel === 'high') return 'high_interest';
      if (lead.interestLevel === 'medium') return 'interested';
      if (lead.interestLevel === 'low') return 'low_interest';
      return 'new';
    case 'potential':
      return 'new';
    default:
      break;
  }

  if (lead.interestLevel === 'high') return 'high_interest';
  if (lead.interestLevel === 'medium') return 'interested';
  if (lead.interestLevel === 'low') return 'low_interest';

  const summary = (lead.aiSummary || '').toLowerCase();
  if (summary.includes('not interested')) return 'not_interested';
  if (summary.includes('unresponsive')) return 'unresponsive';
  if (summary.includes('engaged') || summary.includes('qualified')) return 'interested';

  return 'unknown';
};

const getRecentLeadsData = (leads: any[]) => {
  const sortedLeads = [...leads]
    .sort((a, b) => {
      const dateA = new Date(a.updatedAt || a.lastContact || Date.now()).getTime();
      const dateB = new Date(b.updatedAt || b.lastContact || Date.now()).getTime();
      return dateB - dateA;
    });

  const deduped: any[] = [];
  const seenIds = new Set<string>();

  for (const lead of sortedLeads) {
    if (!lead || !lead.id || seenIds.has(lead.id)) {
      continue;
    }
    if (!lead.customerName) {
      continue;
    }
    seenIds.add(lead.id);
    deduped.push({
      date: new Date(lead.updatedAt || lead.lastContact || Date.now()).toLocaleDateString(),
      customerName: lead.customerName,
      status: resolveRecentLeadStatus(lead),
    });
    if (deduped.length === 5) {
      break;
    }
  }

  return deduped;
};

// Chart data will be calculated from real data
const getEmptyChartData = () => [
  { name: 'Mon', leads: 0, converted: 0 },
  { name: 'Tue', leads: 0, converted: 0 },
  { name: 'Wed', leads: 0, converted: 0 },
  { name: 'Thu', leads: 0, converted: 0 },
  { name: 'Fri', leads: 0, converted: 0 },
  { name: 'Sat', leads: 0, converted: 0 },
  { name: 'Sun', leads: 0, converted: 0 },
];

const getInterestColor = (level: string) => {
  switch (level) {
    case 'high': return '#4caf50';
    case 'medium': return '#ff9800';
    case 'low': return '#f44336';
    default: return '#757575';
  }
};

const getChannelIcon = (channel: string) => {
  switch (channel) {
    case 'whatsapp': return <WhatsApp sx={{ fontSize: 16 }} />;
    case 'sms': return <Sms sx={{ fontSize: 16 }} />;
    case 'phone': return <Phone sx={{ fontSize: 16 }} />;
    default: return <Message sx={{ fontSize: 16 }} />;
  }
};

const DashboardHome: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const { unassignedLeads, myLeads, assignLeadToMe, assignLeadToAgent, initializeLeads } = useLeadStore();
  const { staffMembers, getActiveStaffMembers } = useStaffStore();
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [selectedLead, setSelectedLead] = useState<any>(null);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [selectedAgent, setSelectedAgent] = useState('');
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const activeStaff = getActiveStaffMembers();
  const statsCards = getStatsCards(unassignedLeads.length + myLeads.length);
  const conversionData = getEmptyChartData();
  const recentLeadsData = getRecentLeadsData([...unassignedLeads, ...myLeads]);

  const renderRecentStatusChip = (status: string) => {
    const normalized = status.toLowerCase();
    const baseChip = {
      size: 'small' as const,
      sx: {
        fontWeight: 600,
      },
    };

    switch (normalized) {
      case 'high_interest':
        return (
          <Chip
            {...baseChip}
            label="High Interest"
            icon={<CheckCircle sx={{ fontSize: 16 }} />}
            sx={{ ...baseChip.sx, bgcolor: '#E8F5E9', color: '#388E3C' }}
          />
        );
      case 'interested':
        return (
          <Chip
            {...baseChip}
            label="Interested"
            icon={<CheckCircle sx={{ fontSize: 16 }} />}
            sx={{ ...baseChip.sx, bgcolor: '#E8F5E9', color: '#4CAF50' }}
          />
        );
      case 'low_interest':
        return (
          <Chip
            {...baseChip}
            label="Low Interest"
            icon={<CheckCircle sx={{ fontSize: 16 }} />}
            sx={{ ...baseChip.sx, bgcolor: '#FFF3E0', color: '#FB8C00' }}
          />
        );
      case 'qualified':
        return (
          <Chip
            {...baseChip}
            label="Qualified"
            icon={<Handshake sx={{ fontSize: 16 }} />}
            sx={{ ...baseChip.sx, bgcolor: '#E3F2FD', color: '#1E88E5' }}
          />
        );
      case 'engaging':
        return (
          <Chip
            {...baseChip}
            label="Engaging"
            icon={<Message sx={{ fontSize: 16 }} />}
            sx={{ ...baseChip.sx, bgcolor: '#E8EAF6', color: '#3949AB' }}
          />
        );
      case 'assigned':
        return (
          <Chip
            {...baseChip}
            label="Assigned"
            icon={<AssignmentInd sx={{ fontSize: 16 }} />}
            sx={{ ...baseChip.sx, bgcolor: '#F3E5F5', color: '#8E24AA' }}
          />
        );
      case 'in_contact':
        return (
          <Chip
            {...baseChip}
            label="In Contact"
            icon={<ContactPhone sx={{ fontSize: 16 }} />}
            sx={{ ...baseChip.sx, bgcolor: '#E0F2F1', color: '#00796B' }}
          />
        );
      case 'not_interested':
        return (
          <Chip
            {...baseChip}
            label="Not Interested"
            icon={<Cancel sx={{ fontSize: 16 }} />}
            sx={{ ...baseChip.sx, bgcolor: '#FFEBEE', color: '#D32F2F' }}
          />
        );
      case 'unresponsive':
        return (
          <Chip
            {...baseChip}
            label="Unresponsive"
            icon={<DoNotDisturb sx={{ fontSize: 16 }} />}
            sx={{ ...baseChip.sx, bgcolor: '#FFF3E0', color: '#F57C00' }}
          />
        );
      case 'new':
        return (
          <Chip
            {...baseChip}
            label="New"
            icon={<PersonAdd sx={{ fontSize: 16 }} />}
            sx={{ ...baseChip.sx, bgcolor: '#E3F2FD', color: '#1976D2' }}
          />
        );
      default:
        return (
          <Chip
            {...baseChip}
            label="Unknown"
            sx={{ ...baseChip.sx, bgcolor: '#F5F5F5', color: '#616161' }}
          />
        );
    }
  };

  useEffect(() => {
    initializeLeads();
  }, [initializeLeads]);

  const handleAssignClick = (event: React.MouseEvent<HTMLElement>, lead: any) => {
    setAnchorEl(event.currentTarget);
    setSelectedLead(lead);
  };

  const handleAssignToMe = async () => {
    if (selectedLead) {
      // Use the store to handle assignment
      await assignLeadToMe(selectedLead.id, user?.name || 'Demo Agent');
      setAnchorEl(null);
      setSelectedLead(null);
    }
  };

  const handleAssignToOther = () => {
    setAnchorEl(null);
    setAssignDialogOpen(true);
  };

  const handleConfirmAssign = async () => {
    if (selectedLead && selectedAgent) {
      // Use the store to handle assignment
      const staff = staffMembers.find(m => m.id === selectedAgent);
      if (staff) {
        await assignLeadToAgent(selectedLead.id, staff.name);
      }
      setAssignDialogOpen(false);
      setSelectedLead(null);
      setSelectedAgent('');
    }
  };

  return (
    <Box>
      {/* Header with Upload Button */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" fontWeight="bold">Dashboard</Typography>
        <Button
          variant="contained"
          startIcon={<CloudUpload />}
          onClick={() => setUploadDialogOpen(true)}
          sx={{ bgcolor: '#1a237e' }}
        >
          Upload Customers
        </Button>
      </Box>

      {/* Unassigned Leads Section - FIRST */}
      <Paper sx={{ p: 3, mb: 3, borderLeft: '4px solid #ff9800' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography variant="h6" fontWeight="bold">
              Unassigned Leads from AI Bot
            </Typography>
            <Chip
              label={`${unassignedLeads.length} new`}
              color="warning"
              size="small"
            />
          </Box>
        </Box>

        {unassignedLeads.length > 0 ? (
          <Grid container spacing={2}>
            {unassignedLeads.map((lead) => (
              <Grid item xs={12} md={4} key={lead.id}>
                <Card sx={{
                  position: 'relative',
                  '&:hover': { boxShadow: 3 },
                  cursor: 'pointer'
                }}
                onClick={(e) => {
                  // Don't navigate if clicking on the assign button
                  if (!(e.target as HTMLElement).closest('button')) {
                    navigate('/conversations');
                  }
                }}>
                  <CardContent>
                    {/* Header */}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Avatar sx={{ width: 32, height: 32, bgcolor: '#1a237e' }}>
                          {lead.customerName.charAt(0)}
                        </Avatar>
                        <Box>
                          <Typography variant="subtitle2" fontWeight="bold">
                            {lead.customerName}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {lead.lastContact}
                          </Typography>
                        </Box>
                      </Box>
                      {getChannelIcon(lead.channel)}
                    </Box>

                    {/* Vehicle & Scenario */}
                    <Box sx={{ mb: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                        <DirectionsCar sx={{ fontSize: 16, color: 'text.secondary' }} />
                        <Typography variant="body2" color="text.secondary">
                          {lead.vehicle}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Chip
                          label={lead.scenario}
                          size="small"
                          sx={{ height: 22 }}
                        />
                        <Chip
                          label={lead.interestLevel}
                          size="small"
                          sx={{
                            height: 22,
                            bgcolor: getInterestColor(lead.interestLevel),
                            color: 'white'
                          }}
                        />
                      </Box>
                    </Box>

                    {/* AI Summary */}
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                      {lead.aiSummary}
                    </Typography>

                    {/* Action Button */}
                    <Button
                      fullWidth
                      variant="contained"
                      startIcon={<AssignmentInd />}
                      onClick={(e) => handleAssignClick(e, lead)}
                      sx={{ bgcolor: '#ff9800' }}
                    >
                      Assign Lead
                    </Button>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        ) : (
          <Typography variant="body2" color="text.secondary">
            No unassigned leads at the moment
          </Typography>
        )}
      </Paper>

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        {statsCards.map((stat, index) => (
          <Grid item xs={12} sm={6} md={3} key={index}>
            <Tooltip title={stat.description} arrow placement="top">
              <Card sx={{ height: '100%', cursor: 'help', '&:hover': { boxShadow: 3 } }}>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                    <Avatar sx={{ bgcolor: `${stat.color}20`, color: stat.color }}>
                      {stat.icon}
                    </Avatar>
                    <Chip
                      label={stat.change}
                      size="small"
                      icon={stat.trend === 'up' ? <ArrowUpward /> : <ArrowDownward />}
                      sx={{
                        bgcolor: stat.trend === 'up' ? '#E8F5E9' : '#FFEBEE',
                        color: stat.trend === 'up' ? '#4CAF50' : '#F44336',
                        fontWeight: 600,
                      }}
                    />
                  </Box>
                  <Typography variant="h4" fontWeight="bold" gutterBottom>
                    {stat.value}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {stat.title}
                  </Typography>
                </CardContent>
              </Card>
            </Tooltip>
          </Grid>
        ))}
      </Grid>

      {/* Charts Row */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        {/* Conversion Chart */}
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 3, height: 400 }}>
            <Typography variant="h6" gutterBottom>
              Weekly Conversion Trends
            </Typography>
            <ResponsiveContainer width="100%" height="90%">
              <LineChart data={conversionData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis />
                <RechartsTooltip />
                <Line
                  type="monotone"
                  dataKey="leads"
                  stroke="#1a237e"
                  strokeWidth={2}
                  name="Total Leads"
                />
                <Line
                  type="monotone"
                  dataKey="converted"
                  stroke="#4CAF50"
                  strokeWidth={2}
                  name="Converted"
                />
              </LineChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>

        {/* Lead Sources Table */}
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 3, height: 400, overflow: 'auto' }}>
            <Typography variant="h6" gutterBottom>
              Lead Sources
            </Typography>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Import Date</TableCell>
                    <TableCell>Filename</TableCell>
                    <TableCell align="right">Count</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {leadSourcesData.map((source, index) => (
                    <TableRow key={index} hover>
                      <TableCell>{source.importDate}</TableCell>
                      <TableCell sx={{ fontSize: '0.85rem' }}>{source.filename}</TableCell>
                      <TableCell align="right">
                        <Chip
                          label={source.count}
                          size="small"
                          color="primary"
                          variant="outlined"
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>
      </Grid>

      {/* Recent Activity and Performance */}
      <Grid container spacing={3}>
        {/* Recent Leads */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, height: 400, overflow: 'auto' }}>
            <Typography variant="h6" gutterBottom>
              Recent Leads
            </Typography>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Date</TableCell>
                    <TableCell>Customer Name</TableCell>
                    <TableCell>Interest Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {recentLeadsData.map((lead, index) => (
                    <TableRow key={index} hover>
                      <TableCell>{lead.date}</TableCell>
                      <TableCell>{lead.customerName}</TableCell>
                      <TableCell>{renderRecentStatusChip(lead.status)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>

        {/* Agent Performance */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, height: 400, overflow: 'auto' }}>
            <Typography variant="h6" gutterBottom>
              Top Performing Agents
            </Typography>
            <List>
              {activeStaff.length > 0 ? (
                activeStaff.slice(0, 4).map((agent, index) => (
                  <ListItem key={agent.id} sx={{ px: 0 }}>
                    <ListItemAvatar>
                      <Avatar sx={{ bgcolor: '#4CAF50' }}>{index + 1}</Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={agent.name}
                      secondary={`${agent.currentLeads || 0} active leads • ${agent.role}`}
                    />
                    <Typography variant="subtitle1" fontWeight="bold" color="primary">
                      €0
                    </Typography>
                  </ListItem>
                ))
              ) : (
                <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
                  No staff members added yet
                </Typography>
              )}
            </List>
          </Paper>
        </Grid>
      </Grid>

      {/* Assign Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
      >
        <MenuItem onClick={handleAssignToMe}>
          <PersonAdd sx={{ mr: 1 }} />
          Assign to Me
        </MenuItem>
        <MenuItem onClick={handleAssignToOther}>
          <AssignmentInd sx={{ mr: 1 }} />
          Assign to Team Member
        </MenuItem>
      </Menu>

      {/* Assign to Team Member Dialog */}
      <Dialog open={assignDialogOpen} onClose={() => setAssignDialogOpen(false)}>
        <DialogTitle>Assign Lead to Team Member</DialogTitle>
        <DialogContent sx={{ minWidth: 300, pt: 2 }}>
          <FormControl fullWidth>
            <InputLabel>Select Agent</InputLabel>
            <Select
              value={selectedAgent}
              label="Select Agent"
              onChange={(e) => setSelectedAgent(e.target.value)}
            >
              {activeStaff.map((member) => (
                <MenuItem key={member.id} value={member.id}>
                  {member.name} ({member.role})
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAssignDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleConfirmAssign} variant="contained" disabled={!selectedAgent}>
            Assign
          </Button>
        </DialogActions>
      </Dialog>

      {/* Customer Upload Dialog */}
      <CustomerUpload
        open={uploadDialogOpen}
        onClose={() => setUploadDialogOpen(false)}
        onSuccess={() => {
          setUploadDialogOpen(false);
          // Refresh data after successful upload
          window.location.reload();
        }}
      />
    </Box>
  );
};

export default DashboardHome;
